import type { RegistrationForm } from "@/adapters/inbound/auth/Registration";
import type {
  RegistrationAttempt,
} from "@/domain/model/Auth";
import type { RegistrationStore } from "@/domain/port/RegistrationStore";

const STORAGE_KEY = "registration-draft-v1";
const ATTEMPT_KEY = "registration-attempt-v1";

export class RegistrationStoreImpl implements RegistrationStore {
  loadRegistrationDraft(): Partial<RegistrationForm> | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as Partial<RegistrationForm>;
    } catch {
      return null;
    }
  }

  saveRegistrationDraft(values: Partial<RegistrationForm>): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    } catch {
      // ignore storage issues
    }
  }

  clearRegistrationDraft(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore storage issues
    }
  }

  loadLatestAttempt(): RegistrationAttempt | null {
    try {
      const raw = sessionStorage.getItem(ATTEMPT_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);

      if (!this.isValidAttempt(parsed)) return null;

      return parsed;
    } catch {
      return null;
    }
  }

  saveLatestAttempt(attempt: RegistrationAttempt): void {
    try {
      sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(attempt));
    } catch {
      // ignore
    }
  }

  clearLatestAttempt(): void {
    try {
      sessionStorage.removeItem(ATTEMPT_KEY);
    } catch {
      // ignore
    }
  }

  private isValidAttempt(value: unknown): value is RegistrationAttempt {
    if (typeof value !== "object" || value === null) return false;

    const v = value as Record<string, unknown>;

    return (
      typeof v.registrationId === "string" &&
      typeof v.checkoutUrl === "string" &&
      (typeof v.expiresAt === "string" || v.expiresAt === null) &&
      typeof v.startedAt === "string"
    );
  }
}
